// Relacionamento com a cliente, do lado da equipe: avisos, mensagens prontas, link de agendamento,
// quem chamar de volta, lista de espera e avaliações.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, useClients, useMe } from '../lib/data.ts';
import { addDays, ddmm, dayLabel, first, isoLocal, nowLocal, waLink } from '../lib/format.ts';
import { absolute, fill, message, MSG_META, MSG_VARS, sendWhatsApp } from '../lib/messages.ts';
import { useSettingsEdit } from '../lib/settings.ts';
import { Avatar } from '../ui/bits.tsx';
import { Saved } from '../ui/cfg.tsx';
import { BackHeader } from '../ui/Header.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { Empty } from '../ui/Empty.tsx';
import { Sheet } from '../ui/Sheet.tsx';

export const bookingPath = (slug: string) => `/agendar/${slug}`;

// ---------- lembretes: quem avisar amanhã ----------
// O app lista os horários do dia e anota "enviado"; o envio é pelo WhatsApp de quem atende, com o texto pronto.
export interface ReminderRow {
  id: string; date: string; time: string; startsAt: string; status: string; professionalId: string; professional: string;
  clientId: string; client: string; phone: string; whatsapp: boolean; service: string; sentAt: string | null; sentBy: string | null;
}
export const useReminders = (date: string) => useQuery({ queryKey: ['reminders', date], queryFn: () => api.get<ReminderRow[]>(`/reminders?date=${date}`) });

export function Lembretes() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useMe().data;
  const today = nowLocal().date;
  const [date, setDate] = useState(nav.params.date ?? addDays(today, 1));
  const q = useReminders(date);
  const rows = q.data ?? [];
  const all = me ? can(me, 'agendaTodas') : false;
  const sent = rows.filter((r) => r.sentAt).length;
  const text = (r: ReminderRow) => message(me?.salon.settings.messages, 'confirmacao', {
    nome: first(r.client), servico: r.service, profissional: first(r.professional), dia: dayLabel(r.date, today).toLowerCase(), hora: r.time, link: '',
  }) || `Oi, ${first(r.client)}! Confirma seu horário ${dayLabel(r.date, today).toLowerCase()} às ${r.time}?`;
  const mark = async (r: ReminderRow, value: boolean) => {
    try { await api.post(`/appointments/${r.id}/reminder`, { sent: value }); await qc.invalidateQueries({ queryKey: ['reminders', date] }); }
    catch (e) { toast(msg(e), 'warn'); }
  };
  const copy = (r: ReminderRow) => {
    navigator.clipboard?.writeText(text(r)).then(() => toast(`Mensagem de ${first(r.client)} copiada.`)).catch(() => toast('Não consegui copiar. Use o botão do WhatsApp.', 'warn'));
  };
  const days = [today, addDays(today, 1), addDays(today, 2)];
  const label = (d: string) => { const l = dayLabel(d, today); return l[0]!.toUpperCase() + l.slice(1); };
  return (
    <div className="view-enter">
      <BackHeader title="Lembretes" />
      <div className="lm-days" role="group" aria-label="Dia dos lembretes">
        {days.map((d) => <button key={d} className={d === date ? 'on' : ''} aria-pressed={d === date} onClick={() => setDate(d)}>{label(d)}</button>)}
      </div>
      {q.data === undefined ? (q.isError ? <Failed compact error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />) : !rows.length ? (
        <Empty icon="cal" title="Nenhum horário para avisar">Agenda livre {dayLabel(date, today)}, ou os horários já foram atendidos ou cancelados.</Empty>
      ) : <>
        <p className="lm-count"><b>{sent} de {rows.length}</b> {rows.length > 1 ? 'avisadas' : 'avisada'}{sent === rows.length ? ' · tudo certo por hoje' : ''}</p>
        <div className="lm-list">{rows.map((r) => (
          <div key={r.id} className={'lm-row' + (r.sentAt ? ' done' : '')}>
            <button className="lm-who" onClick={() => nav.open({ kind: 'appt', id: r.id })}>
              <span className="lm-t">{r.time}</span>
              <span className="lm-n"><b>{r.client}</b><small>{r.service}{all ? ` · ${first(r.professional)}` : ''}{r.status === 'pending' ? ' · a confirmar' : ''}</small></span>
            </button>
            <div className="lm-act">
              {!r.phone ? <small>Sem celular cadastrado</small> : !r.whatsapp ? <small>Não quer mensagens</small> : r.sentAt ? (
                <><span className="lm-ok"><Icon name="check" />Enviado</span><button className="lm-lnk" onClick={() => void mark(r, false)}>Desfazer</button></>
              ) : (
                <><a className="lm-send" href={waLink(r.phone, text(r))} target="_blank" rel="noopener noreferrer" onClick={() => void mark(r, true)}><Icon name="message" />WhatsApp</a>
                  <button className="lm-lnk" onClick={() => copy(r)}>Copiar</button>
                  <button className="lm-lnk" onClick={() => void mark(r, true)}>Já avisei</button></>
              )}
            </div>
          </div>
        ))}</div>
        <p className="m-hint rg-hint">O texto sai do WhatsApp de quem atende. Ao tocar em WhatsApp, o horário já fica marcado como enviado. Dá para ajustar o texto em Mensagens.</p>
      </>}
      <div style={{ height: 16 }} />
    </div>
  );
}


// ---------- avisos ----------
interface Notif { id: string; kind: string; text: string; appointmentId: string | null; clientId?: string | null; readAt: string | null; createdAt: string }
export const useNotifs = () => useQuery({ queryKey: ['notifs'], queryFn: () => api.get<{ unread: number; items: Notif[] }>('/notifications'), refetchInterval: 60_000 });
const AV_ICON: Record<string, IconName> = { online: 'cal', sinal: 'card', prazo: 'alert', confirmou: 'check', remarcou: 'cal', cancelou: 'info', avaliacao: 'star', anamnese: 'clip' };

export function Bell() {
  const nav = useNav();
  const n = useNotifs().data?.unread ?? 0;
  return (
    <button className="iconbtn bell" aria-label={`Avisos${n ? `, ${n} ${n > 1 ? 'novos' : 'novo'}` : ''}`} onClick={() => nav.open({ kind: 'avisos' })}>
      <Icon name="bell" />{n ? <i className="bell-n">{n > 99 ? '99+' : n}</i> : null}
    </button>
  );
}

export function AvisosSheet() {
  const nav = useNav();
  const qc = useQueryClient();
  const q = useNotifs();
  const items = q.data?.items ?? [];
  const today = nowLocal().date;
  const isToday = (n: Notif) => isoLocal(n.createdAt).date === today;
  const readAll = async () => { await api.post('/notifications/read').catch(() => undefined); await qc.invalidateQueries({ queryKey: ['notifs'] }); };
  const go = (n: Notif) => {
    if (n.appointmentId) return nav.open({ kind: 'appt', id: n.appointmentId });
    if (n.kind === 'avaliacao') return nav.go('avaliacoes');
    if (n.clientId) return nav.go('cliente', { id: n.clientId });
  };
  const row = (n: Notif) => (
    <button key={n.id} className={'gl-row gl-btn av-row' + (n.readAt ? '' : ' nova')} onClick={() => go(n)}>
      <span className="av-ic"><Icon name={AV_ICON[n.kind] ?? 'info'} /></span>
      <b>{n.text}<small>{isToday(n) ? isoLocal(n.createdAt).time : `${ddmm(isoLocal(n.createdAt).date)} · ${isoLocal(n.createdAt).time}`}</small></b>
      {!n.readAt && <i className="av-dot" aria-label="novo" />}
    </button>
  );
  const hoje = items.filter(isToday), antes = items.filter((n) => !isToday(n));
  const unread = q.data?.unread ?? 0;
  return (
    <Sheet title="Avisos" sub={unread ? `${unread} ${unread > 1 ? 'novos' : 'novo'}` : 'tudo lido'}
      actions={<>{unread > 0 && <button className="cta ghost" onClick={readAll}>Marcar todos como lidos</button>}<button className="link-back" onClick={nav.close}>Fechar</button></>}>
      {hoje.length > 0 && <><p className="gl-title">Hoje</p><div className="gl">{hoje.map(row)}</div></>}
      {antes.length > 0 && <><p className="gl-title">Antes</p><div className="gl">{antes.map(row)}</div></>}
      {q.data === undefined ? (q.isError ? <Failed compact error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />) : !items.length && <p className="m-hint rg-hint">Nenhum aviso por enquanto. Agendamentos pelo link, sinais e avaliações aparecem aqui.</p>}
    </Sheet>
  );
}

// ---------- mensagens prontas ----------
export function Mensagens() {
  const { settings, defaults, save, savedAt } = useSettingsEdit();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  if (!settings) return <div className="view-enter"><BackHeader title="Mensagens" /></div>;
  const set = (key: string, v: { on: boolean; text: string }) => save((s) => ({ ...s, messages: { ...s.messages, [key]: v } }));
  return (
    <div className="view-enter">
      <BackHeader title="Mensagens" />
      <div className="note" style={{ margin: '4px var(--gutter) 0' }}><Icon name="info" /><span>Por enquanto, as mensagens saem do WhatsApp de quem atende, com o texto já pronto: é só tocar em enviar. Só para quem autorizou mensagens no cadastro.</span></div>
      <div className="hsec"><div className="hsec-label"><b>Mensagem de confirmação</b><Saved at={savedAt} /></div>
        <div className="jr">{MSG_META.map((m) => {
          const cur = settings.messages[m.key] ?? defaults?.messages[m.key] ?? { on: false, text: '' };
          const isEd = editing === m.key;
          const shown = isEd ? draft : cur.text;
          return (
            <div key={m.key} className={'jr-step' + (cur.on ? '' : ' off')}><div className="jr-rail"><i /></div><div className="jr-body">
              <div className="jr-head"><span><small>{m.when}</small><strong>{m.label}</strong></span>
                <button role="switch" aria-checked={cur.on} aria-label={m.label} onClick={() => set(m.key, { ...cur, on: !cur.on })}><Icon name={cur.on ? 'toggleOn' : 'toggleOff'} /></button></div>
              <p className="jr-desc">{m.desc}</p>
              <div className="wa"><div className="wa-bubble"><p>{fill(shown, m.sample)}</p><span className="wa-time">10:02 ✓✓</span></div></div>
              {isEd ? (
                <div className="jr-edit">
                  <textarea rows={4} aria-label="Texto da mensagem" value={draft} autoFocus onChange={(e) => setDraft(e.target.value)} />
                  <div className="jr-vars"><span>Inserir</span>{MSG_VARS.map(([tok, label]) => <button key={tok} onClick={() => setDraft(draft.replace(/\s*$/, '') + ' ' + tok)}>{label}</button>)}</div>
                  <div className="jr-edit-act">
                    <button onClick={() => setDraft(defaults?.messages[m.key]?.text ?? draft)}>Voltar ao texto original</button>
                    <button className="solid" onClick={() => { if (draft.trim() && draft !== cur.text) set(m.key, { ...cur, text: draft.trim() }); setEditing(null); }}>Pronto</button>
                  </div>
                </div>
              ) : <button className="jr-edit-btn" onClick={() => { setEditing(m.key); setDraft(cur.text); }}>Editar texto</button>}
            </div></div>
          );
        })}</div>
      </div>
      <div style={{ height: 16 }} />
    </div>
  );
}

// ---------- link de agendamento ----------
export function MeuLink() {
  const me = useMe().data!;
  const toast = useToast();
  const { settings } = useSettingsEdit();
  const url = absolute(bookingPath(me.salon.slug));
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast('Link copiado.')).catch(() => toast('Não deu para copiar. Segure o link para copiar.', 'warn'));
  const d = settings?.deposit;
  return (
    <div className="view-enter cp2"><BackHeader title="Link de agendamento" />
      <div className="cp2-body">
        <div className="ml-url"><small>Seu link</small><b>{url.replace(/^https?:\/\//, '')}</b><button className="cta sm" onClick={copy}>Copiar</button></div>
        <p className="gl-title">Como funciona</p>
        <div className="gl">
          <div className="gl-row"><span>Serviços</span><b>só os marcados para o link</b></div>
          <div className="gl-row"><span>Horários</span><b>{settings ? `de ${settings.minAdvanceHours}h a ${settings.maxAdvanceDays} dias de antecedência` : '…'}</b></div>
          <div className="gl-row"><span>Sinal no Pix</span><b>{!d ? '…' : d.mode === 'never' || !d.linkAlways ? 'não pede' : d.kind === 'fixed' ? `R$ ${(d.fixedCents / 100).toFixed(2).replace('.', ',')}` : `${d.bps / 100}% do serviço`}</b></div>
          <div className="gl-row"><span>Remarcar e cancelar</span><b>{settings ? `pelo link, até ${settings.cancelWindowHours}h antes` : '…'}</b></div>
        </div>
        <p className="m-hint">Mude isso em Serviços (o que aparece no link) e em Regras do salão (sinal, antecedência e cancelamento).</p>
        <p className="gl-title">Para divulgar</p>
        <div className="gl">
          <button className="gl-row gl-btn ac-invite" onClick={() => { copy(); toast('Link copiado. Cole na bio do Instagram.'); }}><b>Na bio do Instagram<small>copia o link</small></b><Icon name="chevR" /></button>
          <a className="gl-row gl-btn ac-invite" href={url} target="_blank" rel="noopener"><b>Ver como a cliente vê<small>abre a página do link</small></b><Icon name="chevR" /></a>
        </div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

// ---------- para chamar de volta (manutenção) ----------
const INV_KEY = 'sublime-convites';
function invited(): Record<string, string> { try { return JSON.parse(localStorage.getItem(INV_KEY) ?? '{}'); } catch { return {}; } }
function markInvited(id: string) { try { localStorage.setItem(INV_KEY, JSON.stringify({ ...invited(), [id]: nowLocal().date })); } catch { /* sem armazenamento */ } }

export function RetornoSheet() {
  const nav = useNav();
  const me = useMe().data!;
  const { settings } = useSettingsEdit();
  const list = useClients('', 'maintenance');
  const [inv, setInv] = useState(invited);
  const today = nowLocal().date;
  const recent = (id: string) => !!inv[id] && inv[id]! >= addDays(today, -7);
  const l = list.data ?? [];
  const convidar = (c: (typeof l)[number]) => {
    sendWhatsApp(c.phone, message(settings?.messages, 'manutencao', { nome: first(c.name), servico: c.last_service ?? '', profissional: first(c.last_pro ?? ''), link: me.salon.settings.onlineBooking === false ? '' : absolute(bookingPath(me.salon.slug)) }));
    markInvited(c.id); setInv(invited());
  };
  const due = (c: (typeof l)[number]) => {
    if (!c.last_visit) return '';
    const left = c.return_days - (c.days_away ?? 0);
    return left > 0 ? `vence em ${left} ${left > 1 ? 'dias' : 'dia'}` : left === 0 ? 'vence hoje' : `venceu há ${-left} ${left < -1 ? 'dias' : 'dia'}`;
  };
  return (
    <Sheet title="Para chamar de volta" sub={l.length ? `${l.length} ${l.length > 1 ? 'clientes' : 'cliente'}` : 'ninguém agora'} actions={<button className="link-back" onClick={nav.close}>Fechar</button>}>
      <p className="m-hint an-intro">Clientes com o retorno sugerido do serviço vencendo ou vencido, e sem horário marcado.</p>
      <div className="gl">{l.map((c) => (
        <div key={c.id} className="gl-row ret-row"><Avatar id={c.id} name={c.name} />
          <b>{c.name}<small>{c.last_service} há {c.days_away} dias · <span className={(c.return_days - (c.days_away ?? 0)) < 0 ? 'late' : ''}>{due(c)}</span></small></b>
          {recent(c.id) ? <em className="mais-ver">Convidada</em>
            : c.whatsapp ? <button className="gl-act" onClick={() => convidar(c)}>Convidar</button>
            : <button className="gl-act" onClick={() => nav.open({ kind: 'novo', clientId: c.id })}>Agendar</button>}
        </div>
      ))}</div>
      <p className="m-hint">O convite é a mensagem "Hora da manutenção", com o link de agendamento. Sem WhatsApp autorizado, só dá para agendar.</p>
    </Sheet>
  );
}

// ---------- lista de espera ----------
export interface WaitItem {
  id: string; clientId: string; client: string; phone: string; whatsapp: boolean; serviceId: string; service: string; durationMin: number;
  professionalId: string | null; professional: string | null; dateFrom: string; dateTo: string; notes: string; status: 'waiting' | 'notified';
}
export const useWaitlist = (date?: string) => useQuery({ queryKey: ['waitlist', date ?? ''], queryFn: () => api.get<WaitItem[]>(`/waitlist${date ? `?date=${date}` : ''}`) });

export function ListaEspera() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const { settings } = useSettingsEdit();
  const date = nav.params.date, time = nav.params.time;
  const q = useWaitlist(date);
  const today = nowLocal().date;
  const status = async (w: WaitItem, s: 'notified' | 'booked' | 'removed') => {
    try { await api.post(`/waitlist/${w.id}/status`, { status: s }); await qc.invalidateQueries({ queryKey: ['waitlist'] }); } catch (e) { toast(msg(e), 'warn'); }
  };
  const avisar = (w: WaitItem) => {
    sendWhatsApp(w.phone, message(settings?.messages, 'espera', { nome: first(w.client), servico: w.service, profissional: first(w.professional ?? ''), dia: date ? dayLabel(date, today).toLowerCase() : '', hora: time ?? '' }));
    void status(w, 'notified');
  };
  const period = (w: WaitItem) => (w.dateFrom === w.dateTo ? ddmm(w.dateFrom) : `${ddmm(w.dateFrom)} a ${ddmm(w.dateTo)}`);
  const l = q.data ?? [];
  return (
    <div className="view-enter cp2"><BackHeader title="Lista de espera" label="Voltar" />
      <div className="cp2-body">
        {date && <p className="m-hint">{time ? `Horário livre ${dayLabel(date, today).toLowerCase()} às ${time}.` : `Quem quer um horário ${dayLabel(date, today).toLowerCase()}.`} Quem responder primeiro fica com ele.</p>}
        {l.length ? <div className="gl">{l.map((w) => (
          <div key={w.id} className="gl-row ret-row"><Avatar id={w.clientId} name={w.client} />
            <b>{w.client}<small>{w.service}{w.professional ? ` com ${first(w.professional)}` : ''} · {period(w)}{w.status === 'notified' ? ' · avisada' : ''}</small></b>
            {w.whatsapp ? <button className="gl-act" onClick={() => avisar(w)}>Avisar</button>
              : <button className="gl-act" onClick={() => nav.open({ kind: 'novo', clientId: w.clientId, ...(date ? { date } : {}), ...(time ? { time } : {}) })}>Agendar</button>}
          </div>
        ))}</div> : (q.data === undefined ? (q.isError ? <Failed error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />) : <Empty icon="clip" title="Ninguém na lista de espera">Ao agendar, marque "Quer um horário antes, se abrir?". Quando alguém desmarcar, o app mostra aqui quem cabe no horário.</Empty>)}
        {l.length > 0 && <>
          <p className="gl-title">Já resolvido?</p>
          <div className="gl">{l.map((w) => (
            <div key={w.id} className="gl-row"><span>{first(w.client)}</span>
              <span className="row-acts"><button className="gl-act" onClick={() => status(w, 'booked')}>Marcou</button><button className="gl-act" onClick={() => status(w, 'removed')}>Tirar da lista</button></span></div>
          ))}</div>
        </>}
        {l.length > 0 && <p className="m-hint">Para colocar alguém na lista, marque "Quer um horário antes, se abrir?" ao agendar.</p>}
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

// ---------- avaliações ----------
export function Avaliacoes() {
  const me = useMe().data!;
  const q = useQuery({ queryKey: ['reviews'], queryFn: () => api.get<{ average: number | null; count: number; items: Array<{ id: string; stars: number; comment: string; createdAt: string; client: string; professional: string }> }>('/reviews') });
  const r = q.data;
  return (
    <div className="view-enter cp2"><BackHeader title="Avaliações" />
      <div className="cp2-body">
        {r && r.count > 0 ? <>
          <div className="card rel-card av-rel"><div className="av-sum"><b>{String(r.average).replace('.', ',')}</b>
            <span>média de {r.count} {r.count > 1 ? 'avaliações' : 'avaliação'}<small>{can(me, 'relatorios') ? 'de todo o salão' : 'dos seus atendimentos'} · a nota fica só com a equipe</small></span></div>
            {r.items.map((x) => (
              <div key={x.id} className="av-com"><span aria-label={`${x.stars} de 5`}>{'★'.repeat(x.stars)}{'☆'.repeat(5 - x.stars)}</span>
                {x.comment && <p>“{x.comment}”</p>}<small>{x.client} · com {first(x.professional)} · {ddmm(isoLocal(x.createdAt).date)}</small></div>
            ))}
          </div>
        </> : (q.data === undefined ? (q.isError ? <Failed error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />) : <Empty icon="star" title="Nenhuma avaliação ainda">Depois de cobrar, o recibo tem o botão "Pedir avaliação". As notas e comentários das clientes aparecem aqui.</Empty>)}
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}
